import { useMemo } from "react";
import {
  cn,
  formatCurrency,
  getColorFromCssVariable,
  HorizontalBarsChart,
} from "ui";
import { ChartCard } from "../ChartCard";
import { HorizontalBarsLoadingSkeleton } from "../HorizontalBarsLoadingSkeleton";
import { useDatadogQuery } from "./useDatadogQuery";
import { twoFixedDigit } from "../../../../utils/twoFixedDigit";

type ProvidersData = {
  label: string;
  value: number;
}[];

export function ProvidersChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<ProvidersData>({
      chartType: "TOP_PROVIDERS",
    });

  const color = getColorFromCssVariable("--chart-categorical-1-subtler");

  const { chartData, topProvider } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return { chartData: [], topProvider: null };
    }

    const total = data.reduce((sum, item) => sum + item.value, 0);

    const transformedData = data.map((item) => ({
      label: item.label,
      value: item.value,
      percentage: total > 0 ? (item.value / total) * 100 : 0,
      color,
    }));

    const topProvider = transformedData[0] || null;
    return { chartData: transformedData, topProvider };
  }, [data, color]);

  return (
    <ChartCard
      ref={ref}
      title={
        topProvider
          ? `${twoFixedDigit(topProvider.percentage)}% ${topProvider.label}`
          : "Top"
      }
      subTitle="Providers"
      contentClassName={cn(
        "overflow-y-auto",
        chartData.length > 0 && "h-auto max-h-80"
      )}
      queryStatus={queryStatus}
      error={error}
      loadingSkeleton={<HorizontalBarsLoadingSkeleton />}
      retry={refetch}
    >
      <HorizontalBarsChart
        items={chartData}
        formatValue={(item) =>
          formatCurrency({
            currency: "USD",
            amount: item.value,
            compact: true,
          })
        }
      />
    </ChartCard>
  );
}
